import { useState } from 'react';
import {
  Typography,
  Stack,
  ButtonGroup,
  Button,
  Box,
  Tooltip,
  Divider,
} from '@mui/material';
import defaultInvoice from '@/assets/tanss2weclappDefaultInvoice.json';
import AceEditor from 'react-ace';
import RestoreIcon from '@mui/icons-material/Restore';
import ClearAllIcon from '@mui/icons-material/ClearAll';

import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';

const SECTIONS = {
  SERVICE: 'service',
  TICKET: 'ticket',
  DISTANCE: 'distance',
  ZONE: 'zone',
  MATERIAL: 'material',
};

export default function T2wTemplateSettings({ deployment, onSave }) {
  const [activeSection, setActiveSection] = useState(SECTIONS.SERVICE);
  const [fields, setFields] = useState({
    service: {
      group: deployment?.config?.service?.group || '',
      title: deployment?.config?.service?.title || '',
      description: deployment?.config?.service?.description || '',
      unit: deployment?.config?.service?.unit || '',
    },
    ticket: {
      group: deployment?.config?.ticket?.group || '',
      title: deployment?.config?.ticket?.title || '',
      description: deployment?.config?.ticket?.description || '',
    },
    distance: {
      group: deployment?.config?.distance?.group || '',
      title: deployment?.config?.distance?.title || '',
      description: deployment?.config?.distance?.description || '',
    },
    zone: {
      group: deployment?.config?.zone?.group || '',
      title: deployment?.config?.zone?.title || '',
      description: deployment?.config?.zone?.description || '',
    },
    material: {
      group: deployment?.config?.material?.group || '',
      title: deployment?.config?.material?.title || '',
      description: deployment?.config?.material?.description || '',
    },
    duration: {
      group: deployment?.config?.duration?.group || '',
      title: deployment?.config?.duration?.title || '',
      description: deployment?.config?.duration?.description || '',
    },
    employee: {
      group: deployment?.config?.employee?.group || '',
      title: deployment?.config?.employee?.title || '',
      description: deployment?.config?.employee?.description || '',
    },
    travel: {
      group: deployment?.config?.travel?.group || '',
      title: deployment?.config?.travel?.title || '',
      description: deployment?.config?.travel?.description || '',
    },
  });

  const handleLoadDefaults = () => {
    setFields(defaultInvoice);
  };

  const resetFields = () => {
    setFields({
      service: {
        group: '',
        title: '',
        description: '',
        unit: '',
      },
      ticket: {
        group: '',
        title: '',
        description: '',
      },
      distance: {
        group: '',
        title: '',
        description: '',
      },
      zone: {
        group: '',
        title: '',
        description: '',
      },
      material: {
        group: '',
        title: '',
        description: '',
      },
      duration: {
        group: '',
        title: '',
        description: '',
      },
      employee: {
        group: '',
        title: '',
        description: '',
      },
      travel: {
        group: '',
        title: '',
        description: '',
      },
    });
  };

  const handleChange = (section, field) => (event) => {
    setFields((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: event.target.value,
      },
    }));
  };

  const handleSave = () => {
    onSave(fields);
  };

  const renderFields = (section) => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Positionsgruppe
        </Typography>
        <AceEditor
          mode="text"
          theme="github"
          name="group-editor"
          width="100%"
          height="50px"
          fontSize={14}
          value={fields[section].group}
          onChange={(newValue) =>
            handleChange(section, 'group')({ target: { value: newValue } })
          }
          setOptions={{
            showLineNumbers: true,
            wrap: true,
          }}
          editorProps={{ $blockScrolling: true }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Titel
        </Typography>
        <AceEditor
          mode="text"
          theme="github"
          name="title-editor"
          width="100%"
          height="50px"
          fontSize={14}
          value={fields[section].title}
          onChange={(newValue) =>
            handleChange(section, 'title')({ target: { value: newValue } })
          }
          setOptions={{
            showLineNumbers: true,
            wrap: true,
          }}
          editorProps={{ $blockScrolling: true }}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Beschreibung
        </Typography>
        <AceEditor
          mode="html"
          theme="github"
          name="description-editor"
          width="100%"
          minWidth="400px"
          height="350px"
          fontSize={14}
          value={fields[section].description}
          onChange={(newValue) =>
            handleChange(
              section,
              'description',
            )({ target: { value: newValue } })
          }
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            showLineNumbers: true,
            tabSize: 2,
            wrap: false,
          }}
          editorProps={{ $blockScrolling: true }}
        />
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">Vorlagen für Belege</Typography>
        <Divider />
      </Box>
      <Box sx={{ mb: 4, width: '100%' }}>
        <ButtonGroup variant="outlined" fullWidth>
          <Button
            onClick={() => setActiveSection(SECTIONS.SERVICE)}
            variant={
              activeSection === SECTIONS.SERVICE ? 'contained' : 'outlined'
            }
          >
            Leistungen
          </Button>
          <Button
            onClick={() => setActiveSection(SECTIONS.TICKET)}
            variant={
              activeSection === SECTIONS.TICKET ? 'contained' : 'outlined'
            }
          >
            Tickets
          </Button>
          <Button
            onClick={() => setActiveSection(SECTIONS.DISTANCE)}
            variant={
              activeSection === SECTIONS.DISTANCE ? 'contained' : 'outlined'
            }
          >
            Kilometer
          </Button>
          <Button
            onClick={() => setActiveSection(SECTIONS.ZONE)}
            variant={activeSection === SECTIONS.ZONE ? 'contained' : 'outlined'}
          >
            Fahrtzone
          </Button>
          <Button
            onClick={() => setActiveSection(SECTIONS.MATERIAL)}
            variant={
              activeSection === SECTIONS.MATERIAL ? 'contained' : 'outlined'
            }
          >
            Material
          </Button>
        </ButtonGroup>
      </Box>
      {renderFields(activeSection)}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Tooltip title="Standardwerte laden">
          <Button
            onClick={handleLoadDefaults}
            size="small"
            startIcon={<RestoreIcon />}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Standardwerte
          </Button>
        </Tooltip>
        <Tooltip title="Alle Werte zurücksetzen">
          <Button
            onClick={resetFields}
            size="small"
            startIcon={<ClearAllIcon />}
            color="error"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Zurücksetzen
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ mt: 'auto', ml: 'auto' }} // This pushes the button to the bottom right
        >
          Speichern
        </Button>
      </Box>
    </Box>
  );
}
